import React from 'react';
import {
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  IconButton,
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Monitor as MonitorIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function ServerTableRow({ 
  server, 
  isSelected, 
  isDeleting,
  hasPendingOperation,
  onSelect, 
  onDelete,
  onRefresh,
  showMonitor = true
}) {
  const navigate = useNavigate();
  const theme = useTheme();

  const getStatusChip = () => {
    if (isDeleting || hasPendingOperation) {
      return (
        <Chip
          label="Deleting..."
          size="small"
          color="warning"
          icon={<LinearProgress size={16} />}
        />
      );
    }

    if (server.status === 'Registering') {
      return (
        <Chip
          label="Registering"
          size="small"
          color="warning"
          icon={<HourglassEmptyIcon fontSize="small" />}
        />
      );
    }

    if (server.status === 'Registration Failed') {
      return (
        <Chip
          label="Failed"
          size="small"
          color="error"
          icon={<ErrorIcon fontSize="small" />}
        />
      );
    }

    if (server.status === 'Active') {
      return (
        <Chip
          label="Active"
          size="small"
          color="success"
          icon={<CheckCircleIcon fontSize="small" />}
        />
      );
    }

    return (
      <Chip
        label={server.status || "Unknown"}
        size="small"
        color="default"
      />
    );
  };

  const isMonitorDisabled = server.status === 'Registering' || server.status === 'Registration Failed' || isDeleting || hasPendingOperation;
  const isSelectionDisabled = server.status === 'Registering' || isDeleting || hasPendingOperation;
  const isDeleteDisabled = isDeleting || hasPendingOperation;

  return (
    <TableRow
      hover
      selected={isSelected}
      sx={{ 
        opacity: isDeleteDisabled ? 0.6 : 1,
        transition: 'opacity 0.2s'
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(server.id)}
          disabled={isSelectionDisabled}
        />
      </TableCell>
      
      <TableCell>
        {getStatusChip()}
      </TableCell>
      
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {server.hostname || 'No hostname'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {server.id}
          </Typography>
        </Box>
      </TableCell>
      
      <TableCell>{server.region || 'N/A'}</TableCell>
      
      <TableCell>{server.ipAddress || 'N/A'}</TableCell>
      
      <TableCell>
        <Box>
          {server.cpuModel && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
              CPU: {server.cpuModel.length > 20 ? server.cpuModel.substring(0, 20) + '...' : server.cpuModel}
            </Typography>
          )}
          {server.gpuDisplayText && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'primary.main' }}>
              GPU: {server.gpuDisplayText.length > 30 ? server.gpuDisplayText.substring(0, 30) + '...' : server.gpuDisplayText}
            </Typography>
          )}
          {!server.cpuModel && !server.gpuDisplayText && (
            <Typography variant="caption" color="text.secondary">
              No info
            </Typography>
          )}
        </Box>
      </TableCell>
      
      <TableCell>{server.numOfCpus || server.cpus || 0}</TableCell>
      
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography>{server.numGpus || 0}</Typography>
          {server.gpuBreakdown?.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              {server.gpuBreakdown.map((gpu, index) => (
                <Chip 
                  key={index}
                  label={gpu.count > 1 ? `${gpu.model} ×${gpu.count}` : gpu.model} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: '20px' }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </TableCell>
      
      <TableCell>{server.mem || 0} GB</TableCell>
      
      <TableCell>
        {(() => {
          const storageGB = server.storage || 0;
          if (storageGB >= 1000) {
            return `${(storageGB / 1000).toFixed(1)} TB`;
          }
          return `${storageGB} GB`;
        })()}
      </TableCell>
      
      <TableCell>
        <Stack direction="row" spacing={1}>
          {showMonitor && (
            <Tooltip title="Monitor">
              <IconButton
                size="small"
                onClick={() => navigate(`/monitoring/${server.id}`)}
                disabled={isMonitorDisabled}
              >
                <MonitorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {onDelete && (
            <Tooltip title={isDeleting ? "Deleting..." : "Delete"}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(server.id)}
                disabled={isDeleteDisabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
} 